/**
 * Risk & Compliance seeds (starter-registry plan R3/R4). The
 * audit-readiness starter is the canonical trigger artifact: a plain
 * table (zero commitment) whose content deliberately creates
 * control-shaped records — the strongest depth signal for the Compass
 * ladder. Its catalog card rides COMPASS_ENABLED (owner decision
 * 2026-06-11); the seed itself stays available to deep links.
 */

import type { SeedProvider } from '../SeedRegistry';

const AUDIT_READINESS_RDM = `---
type: table
title: "Audit Readiness Check"
---
table audit_readiness "Audit Readiness — Control Checklist" { columns: ["Not started", "In progress", "Evidence ready"], layout: stack }
sticky c_access "Access control policy — Owner: IT" { cell: "Not started" }
sticky c_vendor "Vendor management register — Owner: Ops" { cell: "Not started" }
sticky c_mfa "MFA enforced for all staff — Owner: IT" { cell: "In progress" }
sticky c_incident "Incident response runbook — Owner: Eng" { cell: "In progress" }
sticky c_backup "Backup & restore test — Owner: Eng" { cell: "Evidence ready" }
`;

const RISK_REGISTER_RDM = `---
type: table
title: "Risk Register"
---
table risks "Risk Register" { columns: ["Identified", "Mitigating", "Accepted"], layout: stack }
sticky r_auth "Auth outage during migration — Impact: high" { cell: "Mitigating" }
sticky r_vendor "SSO vendor renewal delay — Impact: medium" { cell: "Identified" }
sticky r_attrition "Key engineer attrition — Impact: medium" { cell: "Identified" }
sticky r_cve "Third-party CVE exposure — Impact: high" { cell: "Mitigating" }
sticky r_latency "Regional latency on EU traffic — Impact: low" { cell: "Accepted" }
`;

const VENDOR_RISK_MAP_RDM = `---
type: table
title: "Vendor Risk Map"
---
table vendors "Vendor Risk Map" { columns: ["Critical", "Important", "Routine"], layout: stack }
sticky v_cloud "Cloud hosting — data processor, SOC 2 on file" { cell: "Critical" }
sticky v_sso "SSO provider — auth dependency, renewal due" { cell: "Critical" }
sticky v_billing "Billing platform — PCI scope" { cell: "Important" }
sticky v_support "Support desk — customer PII access" { cell: "Important" }
sticky v_swag "Merch supplier — no data access" { cell: "Routine" }
`;

const ISO27001_ANNEX_A_RDM = `---
type: table
title: "ISO 27001 Annex A Checklist"
---
table annex_a "ISO 27001 Annex A — Readiness" { columns: ["Not started", "In progress", "Evidence ready"], layout: stack }
sticky a5 "A.5 Information security policies — Owner: CISO" { cell: "Not started" }
sticky a6 "A.6 Organization of infosec — Owner: CISO" { cell: "Not started" }
sticky a8 "A.8 Asset management — Owner: IT" { cell: "In progress" }
sticky a9 "A.9 Access control — Owner: IT" { cell: "In progress" }
sticky a12 "A.12 Operations security — Owner: Eng" { cell: "Evidence ready" }
sticky a16 "A.16 Incident management — Owner: Eng" { cell: "In progress" }
`;

const ISMS_SCOPE_RDM = `---
type: table
title: "ISMS Scope Map"
---
table isms_scope "ISMS Scope" { columns: ["In scope", "Supporting", "Out of scope"], layout: stack }
sticky s_prod "Production SaaS platform" { cell: "In scope" }
sticky s_data "Customer data stores" { cell: "In scope" }
sticky s_people "Engineering & support staff" { cell: "In scope" }
sticky s_corp "Corporate IT (laptops, SSO)" { cell: "Supporting" }
sticky s_vendors "Critical vendors" { cell: "Supporting" }
sticky s_mkt "Marketing website" { cell: "Out of scope" }
`;

export const auditReadinessSeed: SeedProvider = {
  id: 'audit-readiness-check',
  type: 'flowchart',
  label: 'Audit readiness check',
  provide: () => ({ format: 'rdm', rdm: AUDIT_READINESS_RDM }),
};

export const riskRegisterSeed: SeedProvider = {
  id: 'risk-register',
  type: 'flowchart',
  label: 'Risk register',
  provide: () => ({ format: 'rdm', rdm: RISK_REGISTER_RDM }),
};

export const vendorRiskMapSeed: SeedProvider = {
  id: 'vendor-risk-map',
  type: 'flowchart',
  label: 'Vendor risk map',
  provide: () => ({ format: 'rdm', rdm: VENDOR_RISK_MAP_RDM }),
};

export const iso27001AnnexAChecklistSeed: SeedProvider = {
  id: 'iso27001-annex-a-checklist',
  type: 'flowchart',
  label: 'ISO 27001 Annex A checklist',
  provide: () => ({ format: 'rdm', rdm: ISO27001_ANNEX_A_RDM }),
};

export const ismsScopeMapSeed: SeedProvider = {
  id: 'isms-scope-map',
  type: 'flowchart',
  label: 'ISMS scope map',
  provide: () => ({ format: 'rdm', rdm: ISMS_SCOPE_RDM }),
};
